import React, { useState } from 'react';
import { Search, TrendingUp, Hash, Target, AlertCircle, CheckCircle, XCircle, FileText, BarChart3, Link as LinkIcon } from 'lucide-react';
import Card from '../ui/CardComponent';
import Button from '../ui/ButtonComponent';
import Badge from '../ui/BadgeComponent';

export default function SEOExtension() {
  const [selectedPost, setSelectedPost] = useState<number | null>(1);

  const posts = [
    { id: 1, title: 'Summer Campaign Launch 2024', platform: 'Instagram', score: 92, status: 'Excellent' },
    { id: 2, title: 'Product Update Announcement', platform: 'Facebook', score: 78, status: 'Good' },
    { id: 3, title: 'Industry Insights Report', platform: 'LinkedIn', score: 65, status: 'Fair' },
    { id: 4, title: 'Tutorial: Getting Started Guide', platform: 'YouTube', score: 88, status: 'Good' },
  ];

  const seoMetrics = [
    { label: 'Overall Score', value: 92, max: 100, status: 'excellent', icon: Target },
    { label: 'Keyword Density', value: 8.5, max: 10, status: 'good', icon: Search },
    { label: 'Hashtag Usage', value: 9.2, max: 10, status: 'excellent', icon: Hash },
    { label: 'Content Length', value: 7.8, max: 10, status: 'good', icon: FileText },
  ];

  const keywordAnalysis = [
    { keyword: 'digital marketing', volume: 'High', difficulty: 'Medium', usage: 5, optimal: '3-7', status: 'good' },
    { keyword: 'social media', volume: 'Very High', difficulty: 'Hard', usage: 3, optimal: '2-5', status: 'good' },
    { keyword: 'content strategy', volume: 'Medium', difficulty: 'Easy', usage: 2, optimal: '2-4', status: 'good' },
    { keyword: 'brand awareness', volume: 'High', difficulty: 'Medium', usage: 8, optimal: '3-7', status: 'warning' },
  ];

  const hashtagRecommendations = [
    { tag: '#digitalmarketing', posts: '2.4M', engagement: 'High', score: 95 },
    { tag: '#contentcreator', posts: '1.8M', engagement: 'Very High', score: 92 },
    { tag: '#socialmedia', posts: '3.1M', engagement: 'Medium', score: 88 },
    { tag: '#marketingstrategy', posts: '980K', engagement: 'High', score: 90 },
    { tag: '#branding', posts: '2.2M', engagement: 'Medium', score: 85 },
  ];

  const contentSuggestions = [
    { type: 'error', message: 'Keyword "brand awareness" is overused. Reduce usage to 3-7 occurrences.', priority: 'high' },
    { type: 'warning', message: 'Consider adding more internal links to improve SEO score.', priority: 'medium' },
    { type: 'success', message: 'Hashtag usage is optimal for maximum reach.', priority: 'low' },
    { type: 'warning', message: 'Content length could be improved. Add 50-100 more words.', priority: 'medium' },
  ];

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  // const getScoreBg = (score: number) => {
  //   if (score >= 90) return 'bg-green-50 border-green-200';
  //   if (score >= 70) return 'bg-blue-50 border-blue-200';
  //   if (score >= 50) return 'bg-yellow-50 border-yellow-200';
  //   return 'bg-red-50 border-red-200';
  // };

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-semibold text-[#111827]">SEO Tools</h1>
          <Button variant="primary" size="md">
            <FileText size={16} className="mr-2" />
            Analyze New Post
          </Button>
        </div>
        <p className="text-sm text-[#6B7280]">Optimize your content for better search rankings and engagement</p>
      </div>

      {/* Overall Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        {seoMetrics.map((metric) => {
          const Icon = metric.icon;
          const percentage = (metric.value / metric.max) * 100;
          return (
            <Card key={metric.label} padding="none">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#F9FAFB] border border-[#E5E7EB] rounded flex items-center justify-center">
                    <Icon size={20} className="text-[#6B7280]" />
                  </div>
                </div>
                <div className="text-3xl font-semibold text-[#111827] mb-1">
                  {metric.value}
                  <span className="text-lg text-[#6B7280]">/{metric.max}</span>
                </div>
                <div className="text-sm text-[#6B7280] mb-3">{metric.label}</div>
                <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      metric.status === 'excellent' ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Post Selection & Score */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827] mb-1">Analyzed Posts</h2>
              <p className="text-sm text-[#6B7280]">Select a post to view detailed SEO analysis</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1F2937] text-white text-left text-xs font-medium uppercase tracking-wider">
                    <th className="px-6 py-3">Post Title</th>
                    <th className="px-6 py-3">Platform</th>
                    <th className="px-6 py-3">SEO Score</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E5E7EB]">
                  {posts.map((post) => (
                    <tr
                      key={post.id}
                      className={`hover:bg-[#F9FAFB] transition-colors cursor-pointer ${
                        selectedPost === post.id ? 'bg-[#FEE2E2]' : ''
                      }`}
                      onClick={() => setSelectedPost(post.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-[#111827]">{post.title}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="default">{post.platform}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-2xl font-semibold ${getScoreColor(post.score)}`}>
                          {post.score}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge
                          variant={
                            post.score >= 90 ? 'success' :
                            post.score >= 70 ? 'info' :
                            post.score >= 50 ? 'warning' : 'default'
                          }
                        >
                          {post.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Button variant="ghost" size="sm">View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Keyword Analysis */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827] mb-1">Keyword Analysis</h2>
              <p className="text-sm text-[#6B7280]">Track keyword usage and optimization</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1F2937] text-white text-left text-xs font-medium uppercase tracking-wider">
                    <th className="px-6 py-3">Keyword</th>
                    <th className="px-6 py-3">Search Volume</th>
                    <th className="px-6 py-3">Difficulty</th>
                    <th className="px-6 py-3">Usage</th>
                    <th className="px-6 py-3">Optimal Range</th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E5E7EB]">
                  {keywordAnalysis.map((keyword, index) => (
                    <tr key={index} className="hover:bg-[#F9FAFB] transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-[#111827]">{keyword.keyword}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="default">{keyword.volume}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="default">{keyword.difficulty}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[#111827]">
                        {keyword.usage}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                        {keyword.optimal}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={keyword.status === 'good' ? 'success' : 'warning'}>
                          {keyword.status === 'good' ? 'Optimal' : 'Review'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Content Suggestions */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827] mb-1">Optimization Suggestions</h2>
              <p className="text-sm text-[#6B7280]">Recommendations to improve your SEO score</p>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {contentSuggestions.map((suggestion, index) => {
                  const Icon = suggestion.type === 'error' ? XCircle :
                              suggestion.type === 'warning' ? AlertCircle : CheckCircle;
                  const iconColor = suggestion.type === 'error' ? 'text-red-500' :
                                   suggestion.type === 'warning' ? 'text-yellow-500' : 'text-green-500';
                  const bgColor = suggestion.type === 'error' ? 'bg-red-50' :
                                 suggestion.type === 'warning' ? 'bg-yellow-50' : 'bg-green-50';

                  return (
                    <div key={index} className={`flex items-start gap-3 p-4 rounded-lg ${bgColor}`}>
                      <Icon size={20} className={`${iconColor} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1">
                        <div className="text-sm text-[#111827]">{suggestion.message}</div>
                      </div>
                      <Badge variant={
                        suggestion.priority === 'high' ? 'default' :
                        suggestion.priority === 'medium' ? 'warning' : 'success'
                      } size="sm">
                        {suggestion.priority}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Hashtag Recommendations */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-2 mb-1">
                <Hash className="text-[#6B7280]" size={18} />
                <h2 className="text-lg font-semibold text-[#111827]">Trending Hashtags</h2>
              </div>
              <p className="text-sm text-[#6B7280]">Recommended hashtags for your content</p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {hashtagRecommendations.map((hashtag, index) => (
                  <div key={index} className="p-3 bg-[#F9FAFB] rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-[#111827]">{hashtag.tag}</span>
                      <Badge variant="info" size="sm">{hashtag.score}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#6B7280]">
                      <span>{hashtag.posts} posts</span>
                      <span>•</span>
                      <span>{hashtag.engagement} engagement</span>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="secondary" size="sm" fullWidth className="mt-4">
                View All Hashtags
              </Button>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827]">Quick Actions</h2>
            </div>
            <div className="p-6 space-y-3">
              <Button variant="secondary" size="md" fullWidth>
                <BarChart3 size={16} className="mr-2" />
                Export Report
              </Button>
              <Button variant="secondary" size="md" fullWidth>
                <LinkIcon size={16} className="mr-2" />
                Check Backlinks
              </Button>
              <Button variant="secondary" size="md" fullWidth>
                <TrendingUp size={16} className="mr-2" />
                Competitor Analysis
              </Button>
            </div>
          </Card>

          {/* SEO Score Breakdown */}
          <Card padding="none">
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-semibold text-[#111827]">Score Breakdown</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#6B7280]">Content Quality</span>
                    <span className="text-sm font-semibold text-[#111827]">95/100</span>
                  </div>
                  <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div className="h-2 bg-green-500 rounded-full" style={{ width: '95%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#6B7280]">Keywords</span>
                    <span className="text-sm font-semibold text-[#111827]">88/100</span>
                  </div>
                  <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div className="h-2 bg-blue-500 rounded-full" style={{ width: '88%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#6B7280]">Readability</span>
                    <span className="text-sm font-semibold text-[#111827]">92/100</span>
                  </div>
                  <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div className="h-2 bg-green-500 rounded-full" style={{ width: '92%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#6B7280]">Technical SEO</span>
                    <span className="text-sm font-semibold text-[#111827]">78/100</span>
                  </div>
                  <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div className="h-2 bg-blue-500 rounded-full" style={{ width: '78%' }} />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
